import type { HTMLAttributes, ReactNode } from "react";
import { cn } from "../../lib/cn";
import { Icon, type IconName } from "./Icon";

// ---------------------------------------------------------------------------
// Badge — small status pill. Tone-based color variants.
// ---------------------------------------------------------------------------

type BadgeTone = "neutral" | "accent" | "success" | "warning" | "danger" | "info";

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: BadgeTone;
  iconLeft?: IconName;
  /** Subtle (default) or solid filled. */
  variant?: "subtle" | "solid" | "outline";
  size?: "sm" | "md";
}

const subtleByTone: Record<BadgeTone, string> = {
  neutral: "bg-ink-100 text-ink-700",
  accent: "bg-accent-50 text-accent-700 dark:bg-accent-900/30 dark:text-accent-300",
  success: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
  warning: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  danger: "bg-rose-50 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
  info: "bg-sky-50 text-sky-700 dark:bg-sky-900/30 dark:text-sky-300",
};

const solidByTone: Record<BadgeTone, string> = {
  neutral: "bg-primary text-primary-fg",
  accent: "bg-accent-600 text-white",
  success: "bg-emerald-600 text-white",
  warning: "bg-amber-500 text-white",
  danger: "bg-rose-600 text-white",
  info: "bg-sky-600 text-white",
};

const outlineByTone: Record<BadgeTone, string> = {
  neutral: "border border-ink-200 text-ink-700",
  accent: "border border-accent-200 text-accent-700 dark:border-accent-800 dark:text-accent-300",
  success:
    "border border-emerald-200 text-emerald-700 dark:border-emerald-800 dark:text-emerald-300",
  warning: "border border-amber-200 text-amber-700 dark:border-amber-800 dark:text-amber-300",
  danger: "border border-rose-200 text-rose-700 dark:border-rose-800 dark:text-rose-300",
  info: "border border-sky-200 text-sky-700 dark:border-sky-800 dark:text-sky-300",
};

export function Badge({
  className,
  tone = "neutral",
  iconLeft,
  variant = "subtle",
  size = "sm",
  children,
  ...props
}: BadgeProps) {
  const variantClass =
    variant === "solid"
      ? solidByTone[tone]
      : variant === "outline"
        ? outlineByTone[tone]
        : subtleByTone[tone];
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 whitespace-nowrap rounded-md font-medium",
        size === "sm" ? "h-5 px-1.5 text-xs" : "h-6 px-2 text-xs",
        variantClass,
        className,
      )}
      {...props}
    >
      {iconLeft && <Icon name={iconLeft} size="xs" />}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Spinner — generic loading indicator
// ---------------------------------------------------------------------------

export function Spinner({
  size = "sm",
  className,
  label,
}: {
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
  /** Optional accompanying label rendered to the right of the icon. */
  label?: string;
}) {
  if (label) {
    return (
      <output className={cn("inline-flex items-center gap-2 text-ink-500 text-sm", className)}>
        <Icon name="loader" size={size} className="animate-spin text-ink-400" />
        <span>{label}</span>
      </output>
    );
  }
  return <Icon name="loader" size={size} className={cn("animate-spin text-ink-400", className)} />;
}

// ---------------------------------------------------------------------------
// DeletingOverlay — covers a list row/card while its delete mutation runs.
// Optimistic flow: dialog closes immediately, this overlay sits on the row
// until the mutation settles (cache invalidation removes the row on success,
// overlay clears on error and a toast surfaces the failure).
// ---------------------------------------------------------------------------

export function DeletingOverlay({ label }: { label: string }) {
  return (
    <div
      className="absolute inset-0 z-10 flex animate-fade-in cursor-progress items-center justify-center bg-primary/10 dark:bg-primary/20"
      aria-live="polite"
      aria-busy="true"
    >
      <output className="inline-flex items-center gap-2 rounded-full bg-primary px-3 py-1.5 font-medium text-primary-fg text-xs shadow-sm">
        <Icon name="loader" size="xs" className="animate-spin" />
        <span>{label}</span>
      </output>
    </div>
  );
}

// ---------------------------------------------------------------------------
// EmptyState — consistent "no data" pattern
// ---------------------------------------------------------------------------

export interface EmptyStateProps {
  icon?: IconName;
  title: string;
  description?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function EmptyState({
  icon = "info",
  title,
  description,
  action,
  className,
}: EmptyStateProps) {
  return (
    <div
      className={cn("flex flex-col items-center justify-center px-4 py-12 text-center", className)}
    >
      <div className="mb-4 rounded-full bg-ink-100 p-3">
        <Icon name={icon} size="md" className="text-ink-500" />
      </div>
      <h3 className="font-semibold text-ink-900 text-sm">{title}</h3>
      {description && <p className="mt-1 max-w-sm text-ink-500 text-sm">{description}</p>}
      {action && <div className="mt-4">{action}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avatar — initials-based, no image network calls in v1
// ---------------------------------------------------------------------------

export interface AvatarProps {
  /** Display name; first letter (or initials) shown. */
  name: string;
  /** Optional override for the displayed text. */
  initials?: string;
  size?: "sm" | "md" | "lg";
  className?: string;
}

const avatarSize = {
  sm: "h-6 w-6 text-[10px]",
  md: "h-8 w-8 text-xs",
  lg: "h-10 w-10 text-sm",
} as const;

export function Avatar({ name, initials, size = "md", className }: AvatarProps) {
  const display = initials ?? deriveInitials(name);
  // Stable color based on name hash
  const tone = pickTone(name);
  return (
    <div
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-full font-medium",
        avatarSize[size],
        tone,
        className,
      )}
      aria-label={name}
      title={name}
    >
      {display}
    </div>
  );
}

function deriveInitials(name: string) {
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) {
    const first = parts[0];
    return first ? first.slice(0, 2).toUpperCase() : "?";
  }
  const first = parts[0]?.[0] ?? "";
  const last = parts[parts.length - 1]?.[0] ?? "";
  return (first + last).toUpperCase();
}

const TONES = [
  "bg-accent-100 text-accent-800 dark:bg-accent-900/40 dark:text-accent-200",
  "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200",
  "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-200",
  "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",
  "bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-200",
  "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200",
];

// Solid bar fills aligned with TONES so a member's private-spending segment
// matches their avatar identity. 500-level reads clearly against bg-ink-100.
const FILLS = [
  "bg-accent-500",
  "bg-emerald-500",
  "bg-rose-500",
  "bg-amber-500",
  "bg-sky-500",
  "bg-violet-500",
];

function hashIndex(name: string, mod: number) {
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
  return Math.abs(h) % mod;
}

function pickTone(name: string) {
  return TONES[hashIndex(name, TONES.length)] ?? TONES[0];
}

/** Solid background class matching the member's avatar identity color. */
export function memberFillClass(name: string): string {
  return FILLS[hashIndex(name, FILLS.length)] ?? (FILLS[0] as string);
}
